import {createContext, useContext, type ReactNode} from 'react';

/**
 * Where an export-mode render is headed.
 *
 * - `download`: a self-contained `.html` file, opened in a real browser. It
 *   carries the app's stylesheet and can rely on CSS the browser supports.
 * - `email`: the body of a message, opened in a mail client. Computed styles
 *   are flattened onto the elements afterwards, and anything a client would
 *   mishandle (hidden panels, SVG, absolute positioning) has to not be emitted
 *   in the first place.
 *
 * Only the handful of components where those two destinations genuinely differ
 * should branch on this; everything else asks `useIsExportMode()`.
 */
export type ExportTarget = 'download' | 'email';

/**
 * Marks a subtree as being rendered for HTML export rather than for the live
 * app, and says which kind of export.
 *
 * Components whose behaviour depends on a live browser — portals opened on
 * hover, ResizeObserver-driven sizing, lazily-loaded chunks — cannot work
 * inside an exported artifact and must render a self-contained equivalent
 * instead. They read this flag to decide which way to render.
 *
 * Default is `undefined` (not exporting), so nothing about live rendering
 * changes.
 */
const ExportModeContext = createContext<ExportTarget | undefined>(undefined);

export function ExportModeProvider({
  target = 'download',
  children,
}: {
  readonly target?: ExportTarget;
  readonly children: ReactNode;
}) {
  return (
    <ExportModeContext.Provider value={target}>
      {children}
    </ExportModeContext.Provider>
  );
}

export function useIsExportMode(): boolean {
  return useContext(ExportModeContext) !== undefined;
}

/** The export destination, or `undefined` when rendering the live app. */
export function useExportTarget(): ExportTarget | undefined {
  return useContext(ExportModeContext);
}

/**
 * Class hooks for export-only CSS.
 *
 * Deliberately NOT Tailwind utilities. Tailwind purges against its `content`
 * glob, and more importantly the behaviour these name — revealing a panel on
 * hover of an ancestor — is the one piece the exported file cannot inherit
 * from the app's stylesheet, because live tooltips are portalled by JS rather
 * than styled into place. `exportOnlyCss()` emits the matching rules into the
 * exported document's own <style> block.
 */
export const EXPORT_PEEK_WRAPPER = 'matic-export-peek';
export const EXPORT_PEEK_PANEL = 'matic-export-peek-panel';

/**
 * Marks a provenance affordance — the "Otto's read" flag and the unanchored
 * citation glyph.
 *
 * These earn their place on a surface where they can be hovered, clicked, and
 * traced back to evidence. An email can do none of that, so all they contribute
 * there is a repeated four-word label interrupting every stat tile and list
 * row. The email build strips anything carrying this class; the app and the
 * downloaded export, both of which keep the interaction, are untouched.
 */
export const EXPORT_PROVENANCE_MARK = 'matic-provenance-mark';

/**
 * Plain CSS appended to an exported document, after the app's own stylesheets.
 *
 * Keeps the exported artifact script-free: evidence peeks open on hover via
 * CSS alone, so the file behaves correctly with JavaScript disabled.
 */
export function exportOnlyCss(): string {
  return `
.${EXPORT_PEEK_WRAPPER} {
  position: relative;
  display: inline-block;
}
.${EXPORT_PEEK_PANEL} {
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  width: max-content;
  max-width: 320px;
  z-index: 50;
  text-align: left;
  white-space: normal;
}
.${EXPORT_PEEK_WRAPPER}:hover > .${EXPORT_PEEK_PANEL},
.${EXPORT_PEEK_WRAPPER}:focus-within > .${EXPORT_PEEK_PANEL} {
  display: block;
}
`;
}
